import cn from 'classnames';
import { forwardRef } from 'react';

import { Listbox, ListboxItem } from '../../internal';

import type { ForwardedRef, MouseEvent, ReactElement } from 'react';

import type { DateRangeQuickSelectionsProps } from './date-range-quick-selections.types';

/**
 * DateRangeQuickSelections displays a list of preset date range options
 * for quick selection in a date range picker.
 *
 * @internal
 */
const DateRangeQuickSelections = forwardRef(
  (props: DateRangeQuickSelectionsProps, forwardedRef: ForwardedRef<HTMLUListElement>): ReactElement => {
    const { className, onPresetSelect, presets, selectedPresetId, ...rest } = props;

    const handlePresetClick = (event: MouseEvent<HTMLLIElement>, presetId: string): void => {
      const preset = presets.find((p) => p.id === presetId);
      if (preset && !preset.disabled) {
        event.preventDefault();
        const value = preset.getValue();
        onPresetSelect(presetId, value);
      }
    };

    return (
      <Listbox className={cn('rbui-date-range-quick-selections', className)} ref={forwardedRef} {...rest}>
        {presets.map((preset) => {
          const { disabled, icon, id, label } = preset;
          const isSelected = selectedPresetId === id;
          const classes = cn('rbui-date-range-quick-selections__item', {
            'rbui-date-range-quick-selections__item--selected': isSelected,
          });

          return (
            <ListboxItem
              checkType={isSelected ? 'checkmark' : null}
              className={classes}
              disabled={disabled}
              key={id}
              onClick={(e) => handlePresetClick(e, id)}
              selected={isSelected}
              startIcon={icon}
            >
              {label}
            </ListboxItem>
          );
        })}
      </Listbox>
    );
  },
);

DateRangeQuickSelections.displayName = 'DateRangeQuickSelections';

export default DateRangeQuickSelections;
